import { useCallback, useState } from "react";
import {
  TILE_EVENTS_VERSION,
  EVENTS,
  RequestEventSchema,
} from "@narmi/tile-events";
import { checkVersion } from "./utils";
import type { EventHandlerProps } from "./types";

export function useEventHandler({
  postMessage,
  openFullView,
  onUnhandledMessage,
  onVersionFail,
  refreshAuthorization,
}: EventHandlerProps) {
  const [initialized, setInitialized] = useState(false);
  const [height, setHeight] = useState(0);

  const handleEvent = useCallback(
    async (data: unknown) => {
      let rawData: unknown;
      try {
        rawData = typeof data === "string" ? JSON.parse(data) : data;
      } catch {
        onUnhandledMessage(data);
        return;
      }
      const result = RequestEventSchema.safeParse(rawData);

      if (!result.success) {
        onUnhandledMessage(rawData);
        return;
      }

      const event = result.data;
      const { id, requestId } = event;

      if (id === EVENTS.initialize) {
        const isCompatible = checkVersion(event.tileEventsVersion);
        setInitialized(isCompatible);
        if (!isCompatible) {
          onVersionFail(event.tileEventsVersion, TILE_EVENTS_VERSION);
        }
        postMessage({ id: EVENTS.initialize, requestId, isCompatible });
      } else if (id === EVENTS.setHeight) {
        setHeight(event.height);
        postMessage({ id: EVENTS.setHeight, requestId });
      } else if (id === EVENTS.openFullView) {
        const opened = await openFullView();
        postMessage({ id: EVENTS.openFullView, opened, requestId });
      } else if (id === EVENTS.refreshAuthorization) {
        const code = await refreshAuthorization();
        postMessage({ id: EVENTS.refreshAuthorization, requestId, code });
      }
    },
    [
      postMessage,
      openFullView,
      onUnhandledMessage,
      onVersionFail,
      refreshAuthorization,
    ]
  );

  return { initialized, height, handleEvent };
}
